import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { 
  Award, 
  TrendingUp, 
  Users, 
  Wallet, 
  Cpu, 
  CheckCircle2, 
  ChevronRight, 
  X, 
  ShieldCheck 
} from "lucide-react";

function WelcomeNote({ showModal, handleShowModal, handleSetCookies }) {
  const [imgSrc, setImgSrc] = useState("img/president_steve.png");

  const handleImgError = () => {
    if (imgSrc === "img/president_steve.png") {
      setImgSrc("img/president_steve.jpg");
    } else {
      setImgSrc("https://ui-avatars.com/api/?name=Stephen+Ndili&background=096c3a&color=fff&size=256");
    }
  };

  return (
    <Modal
      size="lg"
      show={showModal}
      onHide={handleShowModal}
      centered
      className="modern-welcome-modal"
      backdrop="static"
    >
      <Modal.Body className="p-0 overflow-hidden" style={{ borderRadius: '24px', border: 'none', background: '#f8fafc' }}>
        <div className="modern-modal-content">
          {/* Header Backdrop */}
          <div 
            style={{ 
              minHeight: '150px', 
              background: 'linear-gradient(135deg, #053520 0%, #096c3a 50%, #0b7e45 100%)',
              position: 'relative',
              padding: '20px 24px',
              borderBottom: '3px solid #c9a227'
            }}
          >
            {/* Top Bar: Official Badge & Close Button */}
            <div className="d-flex align-items-center justify-content-between">
              <div 
                className="d-inline-flex align-items-center gap-2 px-3 py-1 text-white"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '30px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase'
                }}
              >
                <Award size={15} color="#ffd700" />
                <span>Office of the President • LMCS</span>
              </div>

              <button 
                onClick={handleShowModal}
                aria-label="Close modal"
                style={{
                  background: 'rgba(255, 255, 255, 0.18)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(6px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="px-3 px-md-4 px-lg-5 pb-4" style={{ marginTop: '-75px', position: 'relative' }}>
            {/* President Profile Card */}
            <div 
              className="p-3 p-md-4 mb-3 shadow-sm" 
              style={{ 
                background: '#ffffff', 
                borderRadius: '20px', 
                border: '1px solid rgba(9, 108, 58, 0.1)',
                boxShadow: '0 15px 35px -10px rgba(5, 53, 32, 0.12)'
              }}
            >
              <div className="d-flex flex-column flex-sm-row align-items-center gap-3 gap-md-4">
                {/* Photo with double border & shadow */}
                <div 
                  style={{ 
                    width: '130px', 
                    height: '130px', 
                    borderRadius: '50%', 
                    overflow: 'hidden', 
                    border: '4px solid #ffffff', 
                    boxShadow: '0 8px 24px rgba(9, 108, 58, 0.22), 0 0 0 2px #c9a227',
                    flexShrink: 0,
                    background: '#f1f5f9'
                  }}
                >
                  <img 
                    src={imgSrc} 
                    alt="Stephen Ebialim Ndili, Ph.D." 
                    className="w-100 h-100" 
                    style={{ objectFit: 'cover', objectPosition: 'center top' }}
                    onError={handleImgError}
                  />
                </div>

                {/* Identity & Credentials */}
                <div className="text-center text-sm-start flex-grow-1">
                  <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-2 mb-1">
                    <span 
                      className="badge px-2 py-1"
                      style={{
                        background: 'rgba(11, 126, 69, 0.1)',
                        color: '#096c3a',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        borderRadius: '6px',
                        letterSpacing: '0.4px'
                      }}
                    >
                      PRESIDENTIAL DESK
                    </span>
                    <span 
                      className="badge px-2 py-1 text-muted"
                      style={{
                        background: '#f1f5f9',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        borderRadius: '6px'
                      }}
                    >
                      Est. July 14, 2021
                    </span>
                  </div>

                  <h4 
                    className="fw-bold mb-1" 
                    style={{ color: '#064d2a', fontSize: '1.35rem', letterSpacing: '-0.3px' }}
                  >
                    Stephen Ebialim Ndili, Ph.D.
                  </h4>

                  <p 
                    className="mb-2 fw-semibold" 
                    style={{ color: '#334155', fontSize: '0.94rem' }}
                  >
                    President, Lion Multi-Purpose Cooperative Society Limited (LMCS)
                  </p>

                  <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-1">
                    <span 
                      className="badge px-2 py-1" 
                      style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      FCA
                    </span>
                    <span 
                      className="badge px-2 py-1" 
                      style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      CPFA
                    </span>
                    <span 
                      className="badge px-2 py-1" 
                      style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      MILRM
                    </span>
                    <span 
                      className="ms-1 small text-muted d-none d-md-inline"
                      style={{ fontSize: '0.75rem' }}
                    >
                      • Chartered Accountant & Public Finance Expert
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Milestones Bar */}
            <div className="row g-2 mb-3">
              <div className="col-6 col-md-3">
                <div className="welcome-stat-pill p-2 p-md-3 rounded-3 text-center h-100">
                  <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                    <Users size={16} color="#0b7e45" />
                    <span className="fw-bold" style={{ color: '#064d2a', fontSize: '1.05rem' }}>1,450+</span>
                  </div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                    Active Members <br /><span style={{ fontSize: '0.7rem', color: '#096c3a' }}>(from 36 in 2021)</span>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="welcome-stat-pill p-2 p-md-3 rounded-3 text-center h-100">
                  <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                    <Wallet size={16} color="#0b7e45" />
                    <span className="fw-bold" style={{ color: '#064d2a', fontSize: '1.05rem' }}>₦1.5B+</span>
                  </div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                    Robust Asset Base <br /><span style={{ fontSize: '0.7rem', color: '#096c3a' }}>(Financial Powerhouse)</span>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="welcome-stat-pill p-2 p-md-3 rounded-3 text-center h-100">
                  <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                    <Cpu size={16} color="#0b7e45" />
                    <span className="fw-bold" style={{ color: '#064d2a', fontSize: '1.05rem' }}>100%</span>
                  </div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                    Fully Automated <br /><span style={{ fontSize: '0.7rem', color: '#096c3a' }}>(Cloud-based Real-Time)</span>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="welcome-stat-pill p-2 p-md-3 rounded-3 text-center h-100">
                  <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                    <TrendingUp size={16} color="#0b7e45" />
                    <span className="fw-bold" style={{ color: '#064d2a', fontSize: '1.05rem' }}>5 Years</span>
                  </div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                    Of Serving Members <br /><span style={{ fontSize: '0.7rem', color: '#096c3a' }}>(Incorp. July 14, 2021)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Address Body */}
            <div 
              className="welcome-text-scroll p-3 p-md-4 rounded-3" 
              style={{ 
                maxHeight: '430px', 
                overflowY: 'auto', 
                fontSize: '0.96rem', 
                lineHeight: '1.75', 
                color: '#334155',
                background: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Title Banner */}
              <div className="pb-3 mb-3 border-bottom">
                <div 
                  className="small fw-bold text-uppercase mb-1" 
                  style={{ color: '#c9a227', letterSpacing: '1px', fontSize: '0.78rem' }}
                >
                  Official Presidential Address
                </div>
                <h5 
                  className="fw-bold m-0" 
                  style={{ color: '#064d2a', fontSize: '1.15rem', lineHeight: '1.4' }}
                >
                  WELCOME ADDRESS FROM THE PRESIDENT, LION MULTI-PURPOSE COOPERATIVE SOCIETY LIMITED (LMCS)
                </h5>
              </div>

              <p className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '1.02rem' }}>
                Dear Esteemed Members, Partners, and Visitors,
              </p>

              <p>
                It is my honour and pleasure to welcome you to the official platform of <strong>Lion Multi-Purpose Cooperative Society Limited</strong>, a cooperative organisation committed to promoting financial empowerment, collective growth, and improved wellbeing among its members.
              </p>

              <p>
                Cooperative societies have historically provided opportunities for individuals with shared interests and aspirations to work together towards common economic and social goals. At Lion Multi-Purpose Cooperative Society Nigeria Limited, we have embraced this principle by creating a reliable platform where members can access financial services, develop sustainable investment opportunities, and participate actively in building a stronger financial future.
              </p>

              <div 
                className="my-3 p-3 rounded-3"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(9, 108, 58, 0.05) 0%, rgba(201, 162, 39, 0.08) 100%)',
                  borderLeft: '4px solid #0b7e45'
                }}
              >
                <p className="mb-0">
                  <strong>Lion Multi-Purpose Cooperative Society Limited</strong> was incorporated on the <strong>14th day of July 2021</strong> in Enugu State, under the Nigeria Cooperative Societies Act No. 98 of 2004. It is a voluntary association of individuals/legal persons united by common bond, who have come together to pursue their socio-economic goals for their own benefits, with high regards for non-members. Ours is a fully automated society and has been serving its members and the public for about five years. Founded on the principles of mutual assistance and economic empowerment, the cooperative has consistently provided valuable financial services to its members, including savings, loans, and investment opportunities. The cooperative society's membership has surged from just <strong>36 in September 2021</strong> to <strong>over 1,450 by September 2026</strong>. This explosive growth has propelled our asset base to <strong>over ₦1.5billion</strong>, underscoring our transformation into a robust financial powerhouse. Our society is built on a foundation of trust, transparency, and commitment to the financial well-being of our members and non-members alike.
                </p>
              </div>

              <p>
                Our Society was established to protect and advance the collective interests of our members through innovative, transparent, and member-focused services. Recognising the challenges associated with traditional cooperative operations, we have adopted modern technology-driven systems, including online real-time services and cloud-based electronic accounting solutions, to improve accessibility, efficiency, and accountability in managing members’ financial activities.
              </p>

              <p>
                We provide a range of services designed to support our members, including online loan application and disbursement, professional investment appraisal and feasibility studies, real-time monitoring of savings and shareholdings, soft loans without collateral, and affordable financing options. These services reflect our commitment to creating opportunities that support personal development, business growth, and financial stability.
              </p>

              {/* Unique Services Visual Showcase */}
              <div className="my-3 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <ShieldCheck size={18} color="#0b7e45" />
                  <span className="fw-bold" style={{ color: '#064d2a', fontSize: '0.9rem' }}>
                    Flagship Member Services:
                  </span>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div className="welcome-service-item p-2 rounded d-flex align-items-center gap-2">
                      <CheckCircle2 size={16} color="#0b7e45" className="flex-shrink-0" />
                      <span className="small fw-semibold" style={{ color: '#334155' }}>
                        Online loan application and disbursement
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="welcome-service-item p-2 rounded d-flex align-items-center gap-2">
                      <CheckCircle2 size={16} color="#0b7e45" className="flex-shrink-0" />
                      <span className="small fw-semibold" style={{ color: '#334155' }}>
                        Professional investment appraisal & studies
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="welcome-service-item p-2 rounded d-flex align-items-center gap-2">
                      <CheckCircle2 size={16} color="#0b7e45" className="flex-shrink-0" />
                      <span className="small fw-semibold" style={{ color: '#334155' }}>
                        Real-time monitoring of savings & shares
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="welcome-service-item p-2 rounded d-flex align-items-center gap-2">
                      <CheckCircle2 size={16} color="#0b7e45" className="flex-shrink-0" />
                      <span className="small fw-semibold" style={{ color: '#334155' }}>
                        Soft loans without collateral
                      </span>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="welcome-service-item p-2 rounded d-flex align-items-center gap-2">
                      <CheckCircle2 size={16} color="#0b7e45" className="flex-shrink-0" />
                      <span className="small fw-semibold" style={{ color: '#334155' }}>
                        Affordable financing options & minimal interest rates
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p>
                As President, I assure every member and prospective member of our dedication to the principles of ownership, transparency, integrity, and accountability. We will continue to strengthen our systems, improve service delivery, and promote a cooperative environment where every member feels valued and empowered.
              </p>

              {/* 4 Core Principles Pill Row */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="welcome-pillar-pill px-3 py-1 rounded-pill small fw-bold">
                  ✓ Ownership
                </span>
                <span className="welcome-pillar-pill px-3 py-1 rounded-pill small fw-bold">
                  ✓ Transparency
                </span>
                <span className="welcome-pillar-pill px-3 py-1 rounded-pill small fw-bold">
                  ✓ Integrity
                </span>
                <span className="welcome-pillar-pill px-3 py-1 rounded-pill small fw-bold">
                  ✓ Accountability
                </span>
              </div>

              <p>
                I therefore invite you to explore our services, become part of our growing cooperative family, and join us in building a trusted and forward-looking cooperative society that creates lasting value for all members.
              </p>

              <p className="fw-semibold" style={{ color: '#064d2a' }}>
                Once again, welcome to Lion Multi-Purpose Cooperative Society Limited.
              </p>

              {/* Signatory Section */}
              <div 
                className="mt-4 pt-3 border-top"
                style={{ borderColor: 'rgba(9, 108, 58, 0.15)' }}
              >
                <div>
                  <div 
                    style={{ 
                      fontFamily: 'serif', 
                      fontStyle: 'italic', 
                      fontSize: '1.25rem', 
                      color: '#064d2a',
                      marginBottom: '4px' 
                    }}
                  >
                    Stephen E. Ndili
                  </div>
                  <div className="fw-bold" style={{ color: '#064d2a', fontSize: '0.98rem' }}>
                    Stephen Ebialim Ndili, Ph.D., FCA, CPFA, MILRM
                  </div>
                  <div className="text-muted fw-semibold" style={{ fontSize: '0.86rem' }}>
                    President, LMCS
                  </div>
                  <div className="text-muted small" style={{ fontSize: '0.78rem' }}>
                    Lion Multi-Purpose Cooperative Society Limited
                  </div>
                </div>


              </div>
            </div>

            {/* Footer Controls */}
            <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 mt-3 pt-3 border-top">
              <div className="form-check m-0">
                <input 
                  type="checkbox" 
                  className="form-check-input" 
                  id="dontShowAgain" 
                  onChange={handleSetCookies}
                  style={{ cursor: 'pointer' }}
                />
                <label 
                  className="form-check-label small text-muted ms-1" 
                  htmlFor="dontShowAgain" 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Do not show this message again
                </label>
              </div>

              <button 
                onClick={handleShowModal}
                className="welcome-continue-btn btn px-4 py-2 fw-bold d-inline-flex align-items-center gap-2"
                style={{ fontSize: '0.94rem' }}
              >
                <span>Continue to Site</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default WelcomeNote;
